import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, MapPin, ShieldCheck, AlertCircle, CheckCircle2, ShieldAlert } from "lucide-react";
import ThomVerifiedStamp from "@/components/ThomVerifiedStamp";
import { EnvironmentBanner, EnvironmentChip, EnvironmentWatermark } from "@/components/EnvironmentBanner";
import { classifyRecord } from "@/lib/environments";
import { AFRICAN_CITIES_FALLBACK } from "@/lib/countries";

const FALLBACK_IMG = "/catalog/pending-verification.svg";
const LIFECYCLE_LABELS = {
  demonstration: "Demonstration",
  submitted: "Submitted",
  verification_booked: "Verification Booked",
  inspection_in_progress: "Inspection In Progress",
  verification_complete: "Verification Complete",
  digital_passport_issued: "Digital Passport Issued",
  trust_certificate_issued: "Trust Certificate Issued",
  escrow_protected: "Escrow Protected",
  completed_transaction: "Completed Transaction",
};

export default function Marketplace() {
  const { user } = useAuth();
  const isAdmin = user && ["admin", "super_admin", "country_admin", "ops_manager", "executive"].includes(user.role);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("verified");   // verified | awaiting | demo
  const [settings, setSettings] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, city: city || undefined, limit: 100 };
      if (tab === "verified") { params.only_verified = true; }
      else if (tab === "awaiting") { params.status = "submitted"; }
      else if (tab === "demo") { params.only_demo = true; }
      // Non-admin tabs never see demo unless they explicitly picked the demo tab (admin-only anyway).
      if (isAdmin && tab === "verified") params.include_demo = false;
      const r = await api.get("/vehicles", { params });
      setItems(r.data);
    } finally { setLoading(false); }
  };

  const loadSettings = async () => {
    try { const r = await api.get("/marketplace-admin/settings"); setSettings(r.data.settings); }
    catch { setSettings(null); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { loadSettings(); }, []);

  const showBanner = tab === "demo" || (settings?.banner_enabled && items.some(v => v.is_demo));

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Environment banner — Demonstration / Sandbox / Pilot (hidden for production) */}
        {showBanner && (
          <div className="mb-6" data-testid="marketplace-demo-banner">
            <EnvironmentBanner env="demonstration" />
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="kicker mb-1">AutoVerify™ · Marketplace</div>
            <h1 className="display text-4xl md:text-5xl">Every genuine listing is inspected. Every inspection is signed.</h1>
          </div>
          <div className="text-sm text-muted-foreground">
            <span className="mono">{items.length}</span> {tab === "verified" ? "verified" : tab === "awaiting" ? "awaiting verification" : "demonstration"} listings
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-4" data-testid="marketplace-filters">
          <TabBtn on={tab === "verified"} onClick={() => setTab("verified")} testid="filter-verified">Verified Assets</TabBtn>
          <TabBtn on={tab === "awaiting"} onClick={() => setTab("awaiting")} testid="filter-awaiting">Awaiting Verification</TabBtn>
          {isAdmin && <TabBtn on={tab === "demo"} onClick={() => setTab("demo")} testid="filter-demo">Demo Listings (Admin)</TabBtn>}
        </div>

        <div className="grid md:grid-cols-4 gap-3 mb-8 p-4 border border-border bg-secondary/40">
          <div className="md:col-span-2 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search make / model / description" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="market-search-input" />
          </div>
          <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="market-city-select"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities across Africa</SelectItem>
              {AFRICAN_CITIES_FALLBACK.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={load} className="btn-primary" data-testid="market-apply-filters">Apply Filters</button>
        </div>

        {loading ? (
          <div className="text-muted-foreground text-sm">Loading listings…</div>
        ) : items.length === 0 ? (
          <div className="p-16 border border-border text-center">
            <div className="display text-2xl">
              {tab === "verified" && "No verified listings yet"}
              {tab === "awaiting" && "No listings awaiting verification"}
              {tab === "demo" && "No demo listings"}
            </div>
            <p className="text-muted-foreground mt-2">Sellers can list an asset and request a THOM Verification. Once verified, it appears here with a Trust Score and QR certificate.</p>
            <Link to="/register" className="btn-primary mt-6 inline-flex">List your asset</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((v) => <ListingCard key={v.id} v={v} settings={settings} />)}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-6 text-[11px] mono text-muted-foreground border-t border-border pt-4 max-w-4xl leading-relaxed">
            Verification results are based on authorised partner integrations and data sources available at the time of inspection. Coverage of theft, cross-border, finance-lien and recall databases varies by jurisdiction and participating organisations.
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const TabBtn = ({ on, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3 py-2 mono text-[11px] uppercase tracking-widest border-b-2 transition-colors ${on ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-primary"}`}>
    {children}
  </button>
);

const ListingCard = ({ v, settings }) => {
  const isDemo = v.is_demo === true;
  const lifecycleLabel = LIFECYCLE_LABELS[v.lifecycle_state] || (isDemo ? "Demonstration" : "Submitted");
  const isVerified = !isDemo && v.verification_status === "verified";
  const showWatermark = isDemo && (settings?.watermark_enabled !== false);

  return (
    <Link to={`/vehicles/${v.id}`} data-testid={`listing-card-${v.id}`} className="group border border-border bg-card hover:border-primary transition-colors flex flex-col">
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        <img
          src={v.photos?.[0] ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${v.photos[0]}?v=medium` : (v.catalog_photo_url || FALLBACK_IMG)}
          alt={`${v.year || ""} ${v.make} ${v.model}`.trim()}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          loading="lazy"
          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
        />
        {/* Environment watermark — Demonstration / Sandbox / Pilot only */}
        {showWatermark && <EnvironmentWatermark record={v} testid={`demo-watermark-${v.id}`} />}
        {/* Demo pill overlay top-left — no THOM stamp for demo */}
        {isDemo ? (
          <div className="absolute top-2 left-2 mono text-[10px] uppercase tracking-widest px-2 py-1 bg-amber-500 text-black rounded-sm border border-amber-700 shadow-sm" data-testid={`demo-pill-${v.id}`}>Demo Listing</div>
        ) : isVerified ? (
          <div className="absolute top-2 left-2 bg-white/95 backdrop-blur-sm rounded-full p-1 shadow-md" data-testid={`card-stamp-${v.id}`} aria-label="THOM VERIFIED">
            <ThomVerifiedStamp size={56} verificationStatus="verified" certificateId={v.certificate_number} trustScore={v.trust_score} clickable={false} showTooltip />
          </div>
        ) : (
          <div className="absolute top-2 left-2">
            <ThomVerifiedStamp size="xs" verificationStatus={v.verification_status || "unverified"} />
          </div>
        )}
        {/* Trust score — ONLY when genuinely verified */}
        {!isDemo && isVerified && v.trust_score != null && (
          <div className="absolute top-2 right-2 mono text-[11px] px-2 py-1 bg-black/80 text-white rounded-sm">Trust {v.trust_score}</div>
        )}
        {/* Buyer protection — never on demo */}
        {!isDemo && v.buyer_protection && (
          <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest px-2 py-1 bg-primary/95 text-primary-foreground rounded-sm">
            <ShieldCheck className="h-3 w-3" /> Protected Transaction
          </div>
        )}
        {/* Photo source ribbons preserved (illustrative / pending verification) */}
        {v.photo_source === "pending_verification" ? (
          <div className="absolute bottom-2 left-2 mono text-[10px] uppercase tracking-widest px-2 py-1 bg-black/80 text-amber-300 rounded-sm border border-amber-500/40">Image Pending Verification</div>
        ) : v.photo_source && v.photo_source !== "actual" ? (
          <div className="absolute bottom-2 left-2 mono text-[10px] uppercase tracking-widest px-2 py-1 bg-black/70 text-white rounded-sm">Illustrative image</div>
        ) : null}
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex items-baseline justify-between gap-3">
          <div className="display text-lg leading-tight">{v.year} {v.make} {v.model}</div>
          <div className="mono text-primary font-semibold whitespace-nowrap">${Number(v.price_usd).toLocaleString()}</div>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {v.city}</span>
          <span className="mono">{v.mileage_km?.toLocaleString()} km</span>
        </div>
        {/* Lifecycle + environment chips — ALWAYS present (production chip is subtle green) */}
        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${isDemo ? "border-amber-500/50 bg-amber-500/10 text-amber-800" : "border-emerald-500/40 bg-emerald-500/5 text-emerald-800"}`} data-testid={`lifecycle-${v.id}`}>
            {lifecycleLabel}
          </span>
          <EnvironmentChip record={v} testid={`env-chip-card-${v.id}`} />
        </div>
        {/* Trust indicators + fraud risk — HIDDEN for demo listings */}
        {!isDemo && v.trust_indicators?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-2 gap-x-2 gap-y-1" data-testid={`trust-indicators-${v.id}`}>
            {v.trust_indicators.slice(0, 6).map(ti => (
              <div key={ti.key} className={`flex items-center gap-1.5 text-[10px] mono uppercase tracking-tight leading-tight ${ti.ok ? "text-primary" : "text-muted-foreground"}`}>
                {ti.ok ? <CheckCircle2 className="h-3 w-3 shrink-0" /> : <AlertCircle className="h-3 w-3 shrink-0" />}
                <span className="truncate">{ti.label}</span>
              </div>
            ))}
          </div>
        )}
        {!isDemo && v.fraud_risk?.rating && (
          <div className="mt-2 flex items-center justify-between text-[10px] mono uppercase tracking-widest">
            <span className="text-muted-foreground">Fraud Risk</span>
            <span className={`px-1.5 py-0.5 rounded-sm border ${
              v.fraud_risk.rating === "Very Low" || v.fraud_risk.rating === "Low"
                ? "text-primary border-primary/40 bg-primary/5"
                : v.fraud_risk.rating === "Moderate"
                ? "text-amber-700 border-amber-400/50 bg-amber-50"
                : "text-red-700 border-red-400/50 bg-red-50"
            }`}>{v.fraud_risk.rating}</span>
          </div>
        )}
      </div>
    </Link>
  );
};
